import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { GetLongRunningSurveysSortColumnNames } from '../../../../../common/src/domain/survey/survey/surveyPaths';
import { SurveyStates } from '../../../../../common/src/enums/SurveyStates';
import { Response200 } from '../../../../../common/src/routes/openApiTypes';
import { useTranslate } from '../../../mixins/translate/translate';
import { IStoreState } from '../../../store/IStoreState';
import { openApiFetchGet } from '../../../store/openApiFetch';
import { newLineToBreak } from '../../../utils/htmlHelper';
import { SortColumn, SortDirections, updateSortColumns } from '../../../utils/sorting';
import { EditPanelFilterSelectFromEndpoint } from '../../common/editPanel/EditPanelFilterSelectFromEndpoint';
import { IconEditLink } from '../../common/iconButtons/IconEditLink';
import {
    Content,
    EditPanelButton,
    EditPanelButtons,
    EditPanelFilterSelect,
    EditPanelInput,
    EditPanelSection,
    PanelBody,
    PanelWithoutForm,
    RightTray,
    Table,
    TableBody,
    TableData,
    TableEmphasizedText,
    TableHead,
    TableHeadRow,
    TableHeader,
    TableRow,
    TableSortableHeader,
    TableSubText,
    Topbar,
    TopbarCrumbActive,
    TopbarLeft,
    Tray,
} from '../../elements';
import { Spinner } from '../../spinners';
import { NullableFormattedDate } from '../../utils/NullableFormattedDate';
import { SurveyStatusLabel } from '../../utils/SurveyStatusLabel';
import { TagLabels } from '../../utils/TagLabels';
import { WorkflowDueDateLabel } from '../../utils/workflowDueDateLabel';

type SearchParameters = {
    name?: string | undefined;
    ownerCompanyId?: string;
    status?: SurveyStates | undefined;
};

type SortColumnName = GetLongRunningSurveysSortColumnNames;

export function LongRunningSurveys() {
    const navigate = useNavigate();
    const translate = useTranslate();
    const useProductTitle = useSelector((state: IStoreState) => state.authentication.useProductTitle);

    const [sortColumns, setSortColumns] = useState<SortColumn<SortColumnName>[]>([
        { columnName: 'objects.name', direction: SortDirections.Descending },
    ]);
    const [searchParameters, setSearchParameters] = useState<SearchParameters>({});
    const [searchForm, setSearchForm] = useState<{ name: string }>({ name: '' });

    const [surveyItems, setSurveyItems] = useState<Response200<'/api/surveys/longRunning', 'get'>>({ items: [] });
    const [isLoading, setIsLoading] = useState(true);

    function search() {
        setSearchParameters((i) => ({ ...i, name: searchForm.name }));
    }

    function handleSortChanged(columnName: SortColumnName, direction: SortDirections | undefined) {
        setSortColumns(updateSortColumns<SortColumnName>(columnName, direction, sortColumns));
    }

    useEffect(() => {
        async function doSearch() {
            setSearchForm({ name: searchParameters.name ?? '' });
            setIsLoading(true);
            const response = await openApiFetchGet('/api/surveys/longRunning', { sortColumns, searchParameters: searchParameters as any });

            setSurveyItems(response);
            setIsLoading(false);
        }
        void doSearch();
    }, [sortColumns, searchParameters]);

    return (
        <>
            <Topbar>
                <TopbarLeft>
                    <TopbarCrumbActive
                        title={useProductTitle ? translate('product.longRunningProducts') : translate('survey.longRunningSurveys')}
                        icon="fa-clock-o"
                    />
                </TopbarLeft>
            </Topbar>
            <Content>
                <Tray>
                    <PanelWithoutForm>
                        <PanelBody noPadding={true}>
                            <Table hover={true}>
                                <TableHead>
                                    <TableHeadRow light={true}>
                                        <TableSortableHeader<SortColumnName>
                                            header={translate('common.name')}
                                            columnName="objects.name"
                                            sortColumns={sortColumns}
                                            onClick={handleSortChanged}
                                        />
                                        <TableSortableHeader<SortColumnName>
                                            header={translate('workflow.assignedTo')}
                                            columnName="users.lastName"
                                            sortColumns={sortColumns}
                                            onClick={handleSortChanged}
                                        />
                                        <TableSortableHeader<SortColumnName>
                                            header={useProductTitle ? translate('common.product') : translate('common.survey')}
                                            columnName="surveys.surveryedOn"
                                            sortColumns={sortColumns}
                                            onClick={handleSortChanged}
                                        />
                                        <TableSortableHeader<SortColumnName>
                                            header={translate('survey.statusUpdatedOn')}
                                            columnName="surveys.statusUpdatedOn"
                                            sortColumns={sortColumns}
                                            onClick={handleSortChanged}
                                        />
                                        <TableSortableHeader<SortColumnName>
                                            header={translate('survey.reportDeadlineOnDate')}
                                            columnName="surveys.reportDeadlineOnDate"
                                            sortColumns={sortColumns}
                                            onClick={handleSortChanged}
                                        />
                                        <TableSortableHeader<SortColumnName>
                                            header={translate('common.status')}
                                            columnName="surveys.status"
                                            sortColumns={sortColumns}
                                            onClick={handleSortChanged}
                                        />
                                        <TableHeader align="right">&nbsp; </TableHeader>
                                    </TableHeadRow>
                                </TableHead>
                                {(isLoading && (
                                    <TableBody>
                                        <TableRow>
                                            <TableData colSpan={5}>
                                                <Spinner />
                                            </TableData>
                                        </TableRow>
                                    </TableBody>
                                )) || (
                                    <TableBody>
                                        {surveyItems.items.map((item) => (
                                            <TableRow onClick={() => navigate(`/surveys/${item.id}`)} key={item.id}>
                                                <TableData>
                                                    <div className="tooltipZXY">
                                                        <TableEmphasizedText text={item.object.name} />
                                                        <TableSubText text={item.description} />
                                                        <TagLabels labels={item.labels} />
                                                        <span className="tooltiptext">
                                                            {item.surveyComment.text
                                                                ? newLineToBreak(item.surveyComment.text)
                                                                : translate('workflow.noComment')}
                                                        </span>
                                                    </div>
                                                </TableData>
                                                <TableData>
                                                    {item.assignedToUser.firstName !== null && item.assignedToUser.lastName !== null && (
                                                        <>
                                                            {item.assignedToUser.firstName} {item.assignedToUser.lastName}
                                                        </>
                                                    )}
                                                </TableData>
                                                <TableData nowrap={true}>
                                                    <NullableFormattedDate value={item.surveryedOn} />
                                                </TableData>
                                                <TableData nowrap={true}>
                                                    <NullableFormattedDate value={item.statusUpdatedOn} />
                                                </TableData>
                                                <TableData nowrap={true}>
                                                    <WorkflowDueDateLabel value={item.reportDeadlineOnDate} />
                                                </TableData>
                                                <TableData>
                                                    <SurveyStatusLabel value={item.status} />
                                                </TableData>
                                                <TableData nowrap={true}>
                                                    {item.permissions.canUpdate && <IconEditLink to={`/surveys/${item.id}/summaryAndAdvice`} />}
                                                </TableData>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                )}
                            </Table>
                        </PanelBody>
                    </PanelWithoutForm>
                </Tray>

                <RightTray>
                    <div className="admin-form">
                        <h4> {useProductTitle ? translate('product.filterProduct') : translate('survey.findSurvey')}</h4>
                        <EditPanelSection topBorder={true}>
                            <EditPanelInput
                                name="objectName"
                                placeholder={translate('common.name')}
                                value={searchForm.name}
                                icon="fa fa-building"
                                handleChange={(value) => setSearchForm((i) => ({ ...i, name: value }))}
                                handleEnter={search}
                                handleBlur={search}
                                disabled={isLoading}
                            />
                        </EditPanelSection>
                        <EditPanelSection>
                            <EditPanelFilterSelectFromEndpoint
                                name="ownerCompanyId"
                                value={searchParameters.ownerCompanyId}
                                onChange={(value) => setSearchParameters((i) => ({ ...i, ownerCompanyId: value }))}
                                disabled={isLoading}
                                placeholder={translate('common.selectItem', translate('common.owner'))}
                                url="/api/companyList"
                                queryParameters={{ type: 'owner' }}
                                mapResponseToOptions={(response: { json: { items: { id: string; name: string }[] } }) =>
                                    response.json.items.map((i) => ({ text: i.name, value: i.id }))
                                }
                            />
                        </EditPanelSection>

                        <EditPanelSection>
                            <EditPanelFilterSelect
                                name="status"
                                value={searchParameters.status}
                                handleChange={(value) => setSearchParameters((i) => ({ ...i, status: value as SurveyStates }))}
                                disabled={isLoading}
                                placeholder={translate('common.selectItem', translate('common.status'))}
                                options={
                                    [
                                        SurveyStates.Open,
                                        SurveyStates.Preparation,
                                        SurveyStates.Conducting,
                                        SurveyStates.Delivery,
                                        SurveyStates.Reporting,
                                        SurveyStates.Completed,
                                    ].map((i) => ({ value: i, text: translate(`surveyStatus.${i}`) })) ?? []
                                }
                            />
                        </EditPanelSection>
                        <EditPanelButtons topBorder>
                            <EditPanelButton onClick={() => setSearchParameters({})} text={translate('common.reset')} />
                            <EditPanelButton onClick={search} text={translate('common.search')} primary />
                        </EditPanelButtons>
                    </div>
                </RightTray>
            </Content>
        </>
    );
}
